





























































































































































































import { AxiosError } from 'axios';
import moment from 'moment';
import { mapActions } from 'vuex';
import { get } from 'vuex-pathify';

import {
  IRequisition,
  IRequisitionComment,
  RequisitionComment,
  RequisitionCommentAttachment,
  RequisitionStatus,
  RequisitionStatusEnum
} from '@/models/requisition';
import { Transition } from '@/models/transition';
import { ErrorManager } from '@/models/error';

import { isHtmlTextEmpty } from '@/utils/helpers';
import auth from '@/mixins/auth';
import colorable from '@/mixins/colorable';
import mixins from '@/utils/mixins';
import ErrorAlert from '../common/ErrorAlert.vue';
import QuillEditor from '../common/QuillEditor.vue';
import RequisitionCommentListItem from './RequisitionCommentListItem.vue';
import CommentAttachmentList from '@/components/common/CommentAttachmentList.vue';
import { IAttachment, IUser } from '@/models';

export default mixins(auth, colorable).extend({
  name: 'RequisitionDetailActivityCard',

  components: {
    ErrorAlert,
    QuillEditor,
    CommentAttachmentList,
    RequisitionCommentListItem
  },

  props: {
    requisition: {
      type: Object as () => IRequisition,
      required: true
    }
  },

  data: () => ({
    loading: false,

    errorMessage: '',
    detailErrorMessage: '',
    currentTab: 0,

    requisitionCommentValid: '',
    comment: new RequisitionComment(),
    commentLoading: false,
    commentErrorMessage: '',
    commentDetailErrorMessage: '',

    mentionsLoading: false,

    attachments: [] as IAttachment[]
  }),

  computed: {
    requisitionComments: get<IRequisitionComment[]>(
      'requisition/getRequisitionComments'
    ),
    requisitionActivities: get<Transition[]>(
      'requisition/getRequisitionActivities'
    ),

    mentionableUsers: get<IUser[]>(
      'requisition/getRequisitionMentionableUsers'
    ),

    getLastCreatedRequisitionCommentId: get<number>(
      'requisition/getLastCreatedRequisitionCommentId'
    ),

    RequisitionStatus: () => RequisitionStatus,

    canSaveComment(): boolean {
      return !isHtmlTextEmpty(this.comment.text) && !this.commentLoading;
    }
  },

  methods: {
    timeFromNow: (date: Date | string) => {
      return moment(date).fromNow();
    },

    ...mapActions({
      fetchRequisitionActivities: 'requisition/fetchRequisitionActivities',
      fetchRequisitionComments: 'requisition/fetchRequisitionComments',
      createRequisitionComment: 'requisition/createRequisitionComment',
      fetchRequisitionCommentsMentionableUsers:
        'requisition/fetchRequisitionCommentsMentionableUsers',
      addRequisitionCommentAttachments:
        'requisition/addRequisitionCommentAttachments'
    }),

    addBaseAttachments(files: File[]) {
      this.attachments.push(
        ...files.map(file => new RequisitionCommentAttachment({ file: file }))
      );
    },

    addRequisitionComment() {
      this.clearAllErrors();

      this.commentLoading = true;

      const parsedCommentMentionText = RequisitionComment.parseHtmlToMentionText(
        this.comment.text
      );

      return this.createRequisitionComment({
        requisitionId: this.requisition.id,
        comment: { ...this.comment, text: parsedCommentMentionText }
      })
        .then(async () => {
          this.comment = new RequisitionComment();
          if (this.attachments.length !== 0) {
            await this.addRequisitionCommentAttachments({
              requisitionId: this.requisition.id,
              commentId: this.getLastCreatedRequisitionCommentId,
              attachments: this.attachments
            });
            this.attachments = [];
          }
        })
        .catch(error => {
          if (error.response) {
            if (error.response.data.detail) {
              this.commentDetailErrorMessage = error.response.data.detail;
            }
          }
          this.commentErrorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.commentLoading = false;
        });
    },

    async loadMentionableUsers() {
      this.clearAllErrors();

      this.mentionsLoading = true;
      try {
        await this.fetchRequisitionCommentsMentionableUsers(
          this.requisition.id
        );
      } catch (error) {
        this.commentErrorMessage = ErrorManager.extractApiError(
          error as AxiosError
        );
      } finally {
        this.mentionsLoading = false;
      }
    },

    clearAllErrors(): void {
      this.errorMessage = '';
      this.detailErrorMessage = '';

      this.commentErrorMessage = '';
      this.commentDetailErrorMessage = '';
    },

    getInitials(name: string) {
      return name
        .toUpperCase()
        .split(' ')
        .map(n => n[0])
        .join('');
    },

    //TODO: this should be from the RequisitionStatus model
    getStatusColor(status: number): string {
      switch (status) {
        case RequisitionStatusEnum.Draft:
          return 'grey';

        case RequisitionStatusEnum.Submitted:
          return 'blue';

        case RequisitionStatusEnum.InReview:
        case RequisitionStatusEnum.Approval2Pending:
        case RequisitionStatusEnum.Approval3Pending:
        case RequisitionStatusEnum.Approval4Pending:
          return 'orange';

        case RequisitionStatusEnum.Approved:
          return 'green';

        case RequisitionStatusEnum.Rejected:
          return 'red darken-2';

        case RequisitionStatusEnum.BuyerAssigned:
          return 'blue darken-3';

        case RequisitionStatusEnum.PurchaseInProgress:
          return 'yellow darken-2';

        case RequisitionStatusEnum.PurchaseDelivered:
          return 'success darken-2';

        case RequisitionStatusEnum.Cancelled:
          return 'deep-orange';

        default:
          return 'red';
      }
    },

    //TODO: this should be from the RequisitionStatus model
    getStatusMessage(status: number): string {
      switch (status) {
        case RequisitionStatusEnum.Draft:
          return 'Draft';

        case RequisitionStatusEnum.Submitted:
          return 'Submitted';

        case RequisitionStatusEnum.InReview:
          return 'Review In Progress';

        case RequisitionStatusEnum.Approval2Pending:
          return 'Approval II Pending';

        case RequisitionStatusEnum.Approval3Pending:
          return 'Approval III Pending';

        case RequisitionStatusEnum.Approval4Pending:
          return 'Approval IV Pending';

        case RequisitionStatusEnum.Approved:
          return 'Approved';

        case RequisitionStatusEnum.Rejected:
          return 'Rejected';

        case RequisitionStatusEnum.BuyerAssigned:
          return 'Buyer Assigned';

        case RequisitionStatusEnum.PurchaseInProgress:
          return 'Purchase In Progress';

        case RequisitionStatusEnum.PurchaseDelivered:
          return 'Purchase Complete';

        case RequisitionStatusEnum.Cancelled:
          return 'Cancelled';

        case RequisitionStatusEnum.Received:
          return 'Received';

        default:
          return 'draft';
      }
    }
  },

  async mounted() {
    this.loading = true;
    try {
      await this.fetchRequisitionComments(this.requisition.id);
      await this.fetchRequisitionActivities(this.requisition.id);
    } catch (err) {
      const error = err as AxiosError;
      if (error.response) {
        if (error.response.data.detail) {
          this.detailErrorMessage = error.response.data.detail;
        }
      }
      this.errorMessage = ErrorManager.extractApiError(error);
    } finally {
      this.loading = false;
    }
  }
});
